import {Component, OnDestroy, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {User} from "../../_types/user";
import {AuthService} from "../../_services/auth.service";
import {take} from 'rxjs';
import {Community} from "../../_types/communities";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {ContentService} from "../../_services/content.service";
import {ThemeService} from "../../_services/theme.service";

@Component({
    selector: 'app-settings',
    templateUrl: './settings.component.html',
    styleUrl: './settings.component.scss'
})

export class SettingsComponent implements OnDestroy {
    @ViewChild('textarea', {static: false}) textarea!: HTMLTextAreaElement;
    settingsForm: FormGroup;
    passwordForm: FormGroup;
    image: any;
    currentUser: User
    photoFile: any;
    maxFileSize: number = 10;
    showCropper: boolean = false;
    imageChangedEvent: any = '';
    community: Community | null = null;

    constructor(private formBuilder: FormBuilder,
                private readonly snackBar: MatSnackBar,
                private authService: AuthService,
                private themeService: ThemeService,
                private contentService: ContentService) {
        this.settingsForm = this.formBuilder.group({
            first_name: [''],
            last_name: [''],
            username: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            postcode: [''],
            bio: [''],
            phone_number: [''],
            date_of_birth: [''],
            gender: [''],
            theme_mode: ['Default'],
        });
        this.passwordForm = this.formBuilder.group({
            password: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(32)]],
            confirmPassword: ['', [Validators.required, Validators.minLength(8), Validators.maxLength(32)]],
        });
        this.currentUser = this.authService.getUser();
        if (this.currentUser) {
            this.settingsForm.patchValue({
                first_name: this.currentUser.first_name,
                last_name: this.currentUser.last_name,
                username: this.currentUser.username,
                email: this.currentUser.email,
                postcode: this.currentUser.postcode,
                bio: this.currentUser.bio,
                phone_number: this.currentUser.phone_number,
                date_of_birth: this.currentUser.date_of_birth,
                gender: this.currentUser.gender,
                theme_mode: this.currentUser.theme_mode,
            })
        }

        this.contentService.getCurrentDashboardCommunity()
            .pipe(takeUntilDestroyed())
            .subscribe(community => {
                this.community = community;
            });

        this.settingsForm.get('theme_mode')!.valueChanges.subscribe(r => {
            this.themeService.updateCurrentBrightnessTheme(r)
        })

    }


    ngOnDestroy(): void {
        this.themeService.updateCurrentBrightnessTheme()
    }

    imageCropped(event: any): void {
        this.photoFile = new File([event.blob], "profile_picture.png");
        const reader: FileReader = new FileReader();
        reader.readAsDataURL(event.blob);
        const that: this = this;
        reader.onloadend = function (): void {
            const base64data = reader.result;
            if (base64data) {
                that.image = base64data;
            }
        };
    }

    onFileInput(event: any): void {

        if (!event.target.files[0] || event.target.files[0] == undefined) {
            this.openSnackBar("Error Selecting Image.", 'error');
            return;
        }
        this.photoFile = event.target.files[0];
        var mimeType = this.photoFile?.type;
        if (mimeType?.match(/image\/*/) == null) {
            // check if image
            this.openSnackBar("Only images are supported.", 'error')
            return;
        }

        let filesize: number = parseFloat(((this.photoFile.size / 1024) / 1024).toFixed(1));

        if (filesize > this.maxFileSize) {
            this.openSnackBar('File too large (' + filesize + 'MB) max file size is ' + this.maxFileSize + 'MB', 'error')
            return;
        }
        this.showCropper = true;
        this.imageChangedEvent = event;


        var reader: FileReader = new FileReader();
        reader.readAsDataURL(this.photoFile);
        reader.onload = (_event): void => {
            if (reader.result) {
                this.image = reader.result;
            } else {
                this.openSnackBar("Reader result returned null.", 'error')
            }
        }
    }

    openSnackBar(message: string, type: string): void {
        this.snackBar.open(message, 'Dismiss', {
            duration: 5000,
            panelClass: [type]
        });
    }

    updateProfile(): void {
        let body = this.settingsForm.value;
        if (this.settingsForm.value.date_of_birth) {
            body.date_of_birth = new Date(this.settingsForm.value.date_of_birth)
            body.date_of_birth = body.date_of_birth.toLocaleDateString('en-CA').slice(0, 10);
        }
        this.authService.patchUser(this.clean(body), this.photoFile).pipe(take(1)).subscribe(r => {
            if (r.id) {
                localStorage.setItem('currentUser', JSON.stringify(r));
            } else {
                this.authService.refreshUser();
            }
            this.openSnackBar('Profile Updated Successfully', 'success');
            location.reload();
        }, e => {
            this.openSnackBar(e, 'error');
        })
    }

    clean(obj: any) {
        // remove empty keys
        for (var propName in obj) {
            if (obj[propName] === null || obj[propName] === undefined || obj[propName] === '') {
                delete obj[propName];
            }
        }
        return obj
    }

    updatePassword(): void {
        this.authService.patchUser({password: this.passwordForm.value.password}).pipe(take(1)).subscribe(r => {
            localStorage.setItem('currentUser', JSON.stringify(r));
            this.passwordForm.reset()
            this.openSnackBar('Password Updated Successfully', 'success');
        }, e => {
            this.openSnackBar(e, 'error');
        })
    }
}
